/**
 * @copyright NIIT Technologies Ltd. 2017-18.
 */
// Angular
import { NgModule, NgZone, Component, ElementRef, Renderer, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
// Core
import { ControlComponent } from '../../core/component/control.component';
// JQX Widget
import { jqxGridComponent } from 'jqwidgets-framework/jqwidgets-ts/angular_jqxgrid';

// Control Value Accessor
const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => NgcGridComponent),
    multi: true
};

/**
 * Grid
 */
@Component({
    selector: 'ngc-grid',
    template: `<jqxGrid *ngIf="visible"
                [altrows] = "altrows"
                [altstart] = "altstart"
                [altstep] = "altstep"
                [autoshowloadelement] = "autoshowloadelement"
                [autoshowfiltericon] = "autoshowfiltericon"
                [autoshowcolumnsmenubutton] = "autoshowcolumnsmenubutton"
                [clipboard] = "clipboard"
                [closeablegroups] = "closeablegroups"
                [columnsmenuwidth] = "columnsmenuwidth"
                [columnmenuopening] = "columnmenuopening"
                [columnmenuclosing] = "columnmenuclosing"
                [cellhover] = "cellhover"
                [enablekeyboarddelete] = "enablekeyboarddelete"
                [enableellipsis] = "enableellipsis"
                [enablemousewheel] = "enablemousewheel"
                [enableanimations] = "enableanimations"
                [enabletooltips] = "enabletooltips"
                [enablehover] = "enablehover"
                [enablebrowserselection] = "enablebrowserselection"
                [everpresentrowposition] = "everpresentrowposition"
                [everpresentrowheight] = "everpresentrowheight"
                [everpresentrowactions] = "everpresentrowactions"
                [everpresentrowactionsmode] = "everpresentrowactionsmode"
                [filterrowheight] = "filterrowheight"
                [filtermode] = "filtermode"
                [groupsrenderer] = "groupsrenderer"
                [groupcolumnrenderer] = "groupcolumnrenderer"
                [groupsexpandedbydefault] = "groupsexpandedbydefault"
                [handlekeyboardnavigation] = "handlekeyboardnavigation"
                [pagerrenderer] = "pagerrenderer"
                [rtl] = "rtl"
                [showdefaultloadelement] = "showdefaultloadelement"
                [showfiltercolumnbackground] = "showfiltercolumnbackground"
                [showfiltermenuitems] = "showfiltermenuitems"
                [showpinnedcolumnbackground] = "showpinnedcolumnbackground"
                [showsortcolumnbackground] = "showsortcolumnbackground"
                [showsortmenuitems] = "showsortmenuitems"
                [showgroupmenuitems] = "showgroupmenuitems"
                [showrowdetailscolumn] = "showrowdetailscolumn"
                [showheader] = "showheader"
                [showgroupsheader] = "showgroupsheader"
                [showaggregates] = "showaggregates"
                [showgroupaggregates] = "showgroupaggregates"
                [showeverpresentrow] = "showeverpresentrow"
                [showfilterrow] = "showfilterrow"
                [showemptyrow] = "showemptyrow"
                [showstatusbar] = "showstatusbar"
                [statusbarheight] = "statusbarheight"
                [showtoolbar] = "showtoolbar"
                [selectionmode] = "selectionmode"
                [theme] = "theme"
                [toolbarheight] = "toolbarheight"
                [autoheight] = "autoheight"
                [autorowheight] = "autorowheight"
                [columnsheight] = "columnsheight"
                [deferreddatafields] = "deferreddatafields"
                [groupsheaderheight] = "groupsheaderheight"
                [groupindentwidth] = "groupindentwidth"
                [pagerheight] = "pagerheight"
                [rowsheight] = "rowsheight"
                [scrollbarsize] = "scrollbarsize"
                [scrollmode] = "scrollmode"
                [scrollfeedback] = "scrollfeedback"
                [autosavestate] = "autosavestate"
                [autoloadstate] = "autoloadstate"
                [columns] = "columns"
                [columngroups] = "columngroups"
                [columnsmenu] = "columnsmenu"
                [columnsresize] = "columnsresize"
                [columnsautoresize] = "columnsautoresize"
                [columnsreorder] = "columnsreorder"
                [disabled] = "disabled"
                [editable] = "editable"
                [editmode] = "editmode"
                [filter] = "filter"
                [filterable] = "filterable"
                [groupable] = "groupable"
                [groups] = "groups"
                [horizontalscrollbarstep] = "horizontalscrollbarstep"
                [horizontalscrollbarlargestep] = "horizontalscrollbarlargestep"
                [initrowdetails] = "initrowdetails"
                [keyboardnavigation] = "keyboardnavigation"
                [localization] = "localization"
                [pagesize] = "pagesize"
                [pagesizeoptions] = "pagesizeoptions"
                [pagermode] = "pagermode"
                [pagerbuttonscount] = "pagerbuttonscount"
                [pageable] = "pageable"
                [rowdetails] = "rowdetails"
                [rowdetailstemplate] = "rowdetailstemplate"
                [ready] = "ready"
                [rendered] = "rendered"
                [renderstatusbar] = "renderstatusbar"
                [rendertoolbar] = "rendertoolbar"
                [rendergridrows] = "rendergridrows"
                [sortable] = "sortable"
                [selectedrowindex] = "selectedrowindex"
                [selectedrowindexes] = "selectedrowindexes"
                [source] = "source"
                [sorttogglestates] = "sorttogglestates"
                [updatedelay] = "updatedelay"
                [virtualmode] = "virtualmode"
                [verticalscrollbarstep] = "verticalscrollbarstep"
                [verticalscrollbarlargestep] = "verticalscrollbarlargestep"
                [width] = "width"
                [height] = "height">
                </jqxGrid>`
})
export class NgcGridComponent extends ControlComponent {
    @Input('altrows') altrows: any;
    @Input('altstart') altstart: any;
    @Input('altstep') altstep: any;
    @Input('autoshowloadelement') autoshowloadelement: any;
    @Input('autoshowfiltericon') autoshowfiltericon: any;
    @Input('autoshowcolumnsmenubutton') autoshowcolumnsmenubutton: any;
    @Input('clipboard') clipboard: any;
    @Input('closeablegroups') closeablegroups: any;
    @Input('columnsmenuwidth') columnsmenuwidth: any;
    @Input('columnmenuopening') columnmenuopening: any;
    @Input('columnmenuclosing') columnmenuclosing: any;
    @Input('cellhover') cellhover: any;
    @Input('enablekeyboarddelete') enablekeyboarddelete: any;
    @Input('enableellipsis') enableellipsis: any;
    @Input('enablemousewheel') enablemousewheel: any;
    @Input('enableanimations') enableanimations: any;
    @Input('enabletooltips') enabletooltips: any;
    @Input('enablehover') enablehover: any;
    @Input('enablebrowserselection') enablebrowserselection: any;
    @Input('everpresentrowposition') everpresentrowposition: any;
    @Input('everpresentrowheight') everpresentrowheight: any;
    @Input('everpresentrowactions') everpresentrowactions: any;
    @Input('everpresentrowactionsmode') everpresentrowactionsmode: any;
    @Input('filterrowheight') filterrowheight: any;
    @Input('filtermode') filtermode: any;
    @Input('groupsrenderer') groupsrenderer: any;
    @Input('groupcolumnrenderer') groupcolumnrenderer: any;
    @Input('groupsexpandedbydefault') groupsexpandedbydefault: any;
    @Input('handlekeyboardnavigation') handlekeyboardnavigation: any;
    @Input('pagerrenderer') pagerrenderer: any;
    @Input('rtl') rtl: any;
    @Input('showdefaultloadelement') showdefaultloadelement: any;
    @Input('showfiltercolumnbackground') showfiltercolumnbackground: any;
    @Input('showfiltermenuitems') showfiltermenuitems: any;
    @Input('showpinnedcolumnbackground') showpinnedcolumnbackground: any;
    @Input('showsortcolumnbackground') showsortcolumnbackground: any;
    @Input('showsortmenuitems') showsortmenuitems: any;
    @Input('showgroupmenuitems') showgroupmenuitems: any;
    @Input('showrowdetailscolumn') showrowdetailscolumn: any;
    @Input('showheader') showheader: any;
    @Input('showgroupsheader') showgroupsheader: any;
    @Input('showaggregates') showaggregates: any;
    @Input('showgroupaggregates') showgroupaggregates: any;
    @Input('showeverpresentrow') showeverpresentrow: any;
    @Input('showfilterrow') showfilterrow: any;
    @Input('showemptyrow') showemptyrow: any;
    @Input('showstatusbar') showstatusbar: any;
    @Input('statusbarheight') statusbarheight: any;
    @Input('showtoolbar') showtoolbar: any;
    @Input('selectionmode') selectionmode: any;
    @Input('theme') theme: any;
    @Input('toolbarheight') toolbarheight: any;
    @Input('autoheight') autoheight: any;
    @Input('autorowheight') autorowheight: any;
    @Input('columnsheight') columnsheight: any;
    @Input('deferreddatafields') deferreddatafields: any;
    @Input('groupsheaderheight') groupsheaderheight: any;
    @Input('groupindentwidth') groupindentwidth: any;
    @Input('pagerheight') pagerheight: any;
    @Input('rowsheight') rowsheight: any;
    @Input('scrollbarsize') scrollbarsize: any;
    @Input('scrollmode') scrollmode: any;
    @Input('scrollfeedback') scrollfeedback: any;
    @Input('autosavestate') autosavestate: any;
    @Input('autoloadstate') autoloadstate: any;
    @Input('columns') columns: any;
    @Input('columngroups') columngroups: any;
    @Input('columnsmenu') columnsmenu: any;
    @Input('columnsresize') columnsresize: any;
    @Input('columnsautoresize') columnsautoresize: any;
    @Input('columnsreorder') columnsreorder: any;
    @Input('editable') editable: any;
    @Input('editmode') editmode: any;
    @Input('filter') filter: any;
    @Input('filterable') filterable: any;
    @Input('groupable') groupable: any;
    @Input('groups') groups: any;
    @Input('horizontalscrollbarstep') horizontalscrollbarstep: any;
    @Input('horizontalscrollbarlargestep') horizontalscrollbarlargestep: any;
    @Input('initrowdetails') initrowdetails: any;
    @Input('keyboardnavigation') keyboardnavigation: any;
    @Input('localization') localization: any;
    @Input('pagesize') pagesize: any;
    @Input('pagesizeoptions') pagesizeoptions: any;
    @Input('pagermode') pagermode: any;
    @Input('pagerbuttonscount') pagerbuttonscount: any;
    @Input('pageable') pageable: any;
    @Input('rowdetails') rowdetails: any;
    @Input('rowdetailstemplate') rowdetailstemplate: any;
    @Input('ready') ready: any;
    @Input('rendered') rendered: any;
    @Input('renderstatusbar') renderstatusbar: any;
    @Input('rendertoolbar') rendertoolbar: any;
    @Input('rendergridrows') rendergridrows: any;
    @Input('sortable') sortable: any;
    @Input('selectedrowindex') selectedrowindex: any;
    @Input('selectedrowindexes') selectedrowindexes: any;
    @Input('source') source: any;
    @Input('sorttogglestates') sorttogglestates: any;
    @Input('updatedelay') updatedelay: any;
    @Input('virtualmode') virtualmode: any;
    @Input('verticalscrollbarstep') verticalscrollbarstep: any;
    @Input('verticalscrollbarlargestep') verticalscrollbarlargestep: any;
    @Input('width') width: any;
    @Input('height') height: any;
    @Input('auto-create') autoCreate: boolean = true;

    /**
     * Initialize
     */
    constructor(private ngZone: NgZone, private elementRef: ElementRef, private rendererRef: Renderer) {
        super(ngZone, elementRef, rendererRef);
    }
}

/**
 * Grid Module
 */
@NgModule({
    imports: [CommonModule],
    exports: [NgcGridComponent],
    declarations: [NgcGridComponent, jqxGridComponent]
})
export class NgcGridModule {
}